import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getFileBlob, NotAvailableOffline, setLastPage, setPageCount } from '@/library/library';
import { blobToBytes } from '@/library/bytes';
import type { RenderTarget } from '@/library/pdf';
import type { LibraryFile } from '@/domain/types';
import { Banner } from '@/ui/components/bits';
import {
  documentKind,
  isReadable,
  opaqueReason,
  readDocumentText,
  type DocumentText,
} from '@/library/documents';

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

export function FileViewer({
  file,
  onClose,
  inline = false,
}: {
  file: LibraryFile;
  onClose: () => void;
  /**
   * Shown inside a docked panel rather than over the whole screen, so a deck
   * can be read beside the question that needed it. The difference is only
   * where it sits: the same viewer, the same controls.
   */
  inline?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const disposeRef = useRef<(() => void) | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [text, setText] = useState<DocumentText | null>(null);
  const [page, setPage] = useState(file.lastPage || 1);
  const [pageInput, setPageInput] = useState(String(file.lastPage || 1));
  const [zoomIndex, setZoomIndex] = useState(2);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  /**
   * The scale at which a page exactly fills the width available. A PDF page is
   * about 816 px at scale 1, which overflows a phone and overflows a docked
   * panel badly, so 100% means "fits" and the zoom steps work from there.
   */
  const [fitScale, setFitScale] = useState(1);
  /**
   * Whether the fit has been measured for the document now open. Drawing
   * before it has means drawing twice — once at the wrong size and once at the
   * right one — and pdf.js refuses a second render while the first is running.
   */
  const [fitted, setFitted] = useState(false);
  /**
   * Renders run one after another. Flipping pages quickly, or resizing the
   * panel mid-draw, would otherwise start a render on a canvas that is already
   * being drawn, which pdf.js rejects outright.
   */
  const renderChain = useRef<Promise<unknown>>(Promise.resolve());

  const kind = documentKind(file.mime, file.name);
  const isPdf = kind === 'pdf';
  const isImage = kind === 'image';

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      setOffline(false);
      try {
        const blob = await getFileBlob(file.id, (p) => !cancelled && setProgress(p));
        if (cancelled) return;

        if (isPdf) {
          // pdf.js is ~600 kB. Loading it only when a PDF is actually opened
          // keeps it out of the bundle every other screen has to download.
          const { openPdf } = await import('@/library/pdf');
          const opened = await openPdf(await blobToBytes(blob));
          if (cancelled) {
            opened.dispose();
            return;
          }
          disposeRef.current = opened.dispose;
          setDoc(opened.doc);
          if (opened.doc.numPages !== file.pageCount) void setPageCount(file.id, opened.doc.numPages);
          setPage((p) => Math.min(Math.max(1, p), opened.doc.numPages));
        } else if (isImage) {
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        } else if (isReadable(kind)) {
          // Slide decks and documents are read for their words. The zip
          // reader is only pulled in when one is actually opened.
          setText(await readDocumentText(await blobToBytes(blob), kind));
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotAvailableOffline) setOffline(true);
        else setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  // Tear the worker down when the viewer closes, not on every re-render.
  useEffect(
    () => () => {
      disposeRef.current?.();
      disposeRef.current = null;
    },
    [],
  );

  const draw = useCallback(async () => {
    if (!doc || !canvasRef.current || !fitted) return;
    const canvas = canvasRef.current;
    const scale = fitScale * ZOOM_STEPS[zoomIndex]!;

    const run = renderChain.current
      .catch(() => {})
      .then(async () => {
        const { renderPage } = await import('@/library/pdf');
        const target: RenderTarget = { canvas, scale };
        await renderPage(doc, page, target);
      });
    renderChain.current = run;

    try {
      await run;
    } catch (err) {
      setError((err as Error).message);
    }
  }, [doc, page, zoomIndex, fitScale, fitted]);

  useEffect(() => {
    void draw();
  }, [draw]);

  useEffect(() => {
    setPageInput(String(page));
    if (isPdf) void setLastPage(file.id, page);
  }, [page, file.id, isPdf]);

  /**
   * Keys belong to the page, not to a panel beside it.
   *
   * Docked, this viewer takes none of them: Escape would close the document as
   * well as whatever is actually on top, and the arrows are already how cards
   * are moved through. Full screen there is nothing else they could mean, so
   * both work there. The ‹ and the Prev/Next buttons do the same jobs.
   */
  useEffect(() => {
    if (inline) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setPage((p) => Math.min(p + 1, doc?.numPages ?? p));
      else if (e.key === 'ArrowLeft') setPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, onClose, inline]);

  // Measured when the document opens, and again whenever the panel is resized.
  useEffect(() => {
    const stage = stageRef.current;
    // The frame around the stage, not the stage itself. The stage's own width
    // moves when its contents overflow — zoom in, a scrollbar appears, and it
    // reports less room, which would re-fit the page and undo the zoom.
    const frame = stage?.parentElement;
    if (!doc || !stage || !frame) return;

    let cancelled = false;
    const measure = async () => {
      const first = await doc.getPage(1);
      if (cancelled) return;
      const unscaled = first.getViewport({ scale: 1 }).width;
      const available = frame.clientWidth - 24;
      if (unscaled > 0 && available > 0) {
        setFitScale(Math.min(Math.max(available / unscaled, 0.2), 3));
      }
      setFitted(true);
    };
    void measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => void measure());
    observer.observe(frame);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [doc]);

  // A new document has to be measured again before anything is drawn.
  useEffect(() => setFitted(false), [doc]);

  const pages = doc?.numPages ?? 1;

  return (
    <div
      className={`viewer${inline ? ' viewer--inline' : ''}`}
      role={inline ? 'region' : 'dialog'}
      aria-modal={inline ? undefined : true}
      aria-label={file.name}
    >
      <header className="viewer__bar">
        <button
          className="icon-btn"
          onClick={onClose}
          aria-label={inline ? 'Back to the file list' : 'Close the file'}
        >
          {inline ? '‹' : '✕'}
        </button>
        <span className="viewer__name" title={file.name}>
          {file.name}
        </span>
        <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
          <button
            className="icon-btn"
            aria-label="Zoom out"
            disabled={zoomIndex === 0}
            onClick={() => setZoomIndex((z) => Math.max(0, z - 1))}
          >
            −
          </button>
          <span className="mono" style={{ fontSize: '0.75rem', minWidth: '3.2em', textAlign: 'center' }}>
            {Math.round(ZOOM_STEPS[zoomIndex]! * 100)}%
          </span>
          <button
            className="icon-btn"
            aria-label="Zoom in"
            disabled={zoomIndex === ZOOM_STEPS.length - 1}
            onClick={() => setZoomIndex((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
          >
            +
          </button>
        </div>
      </header>

      <div className="viewer__stage" ref={stageRef}>
        {loading ? (
          <p className="muted">
            {progress ? `Downloading part ${progress.done} of ${progress.total}…` : 'Opening…'}
          </p>
        ) : null}

        {offline ? (
          <div style={{ maxWidth: 420 }}>
            <Banner kind="warn">
              <strong>Not available offline</strong>
              <div>
                This file is not pinned, so its contents are not on this device. Pin it while you
                have a connection and it will open anywhere.
              </div>
            </Banner>
          </div>
        ) : null}

        {error ? (
          <div style={{ maxWidth: 420 }}>
            <Banner kind="error">{error}</Banner>
          </div>
        ) : null}

        {!loading && !offline && !error ? (
          isPdf ? (
            <canvas ref={canvasRef} className="viewer__canvas" />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={file.name}
              className="viewer__image"
              style={{ width: `${ZOOM_STEPS[zoomIndex]! * 100}%` }}
            />
          ) : text ? (
            <DocumentReader text={text} scale={ZOOM_STEPS[zoomIndex]!} />
          ) : (
            <div style={{ maxWidth: 420 }}>
              <Banner kind="warn">
                <strong>Stored, but not readable here</strong>
                <div>{opaqueReason(file.name)}</div>
              </Banner>
            </div>
          )
        ) : null}
      </div>

      {isPdf && doc ? (
        <footer className="viewer__bar viewer__bar--bottom">
          <button className="btn btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </button>
          <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <input
              className="input mono"
              style={{ width: 64, minHeight: 36, textAlign: 'center' }}
              value={pageInput}
              inputMode="numeric"
              aria-label="Page number"
              onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
              onBlur={() => {
                const n = Number(pageInput);
                if (n >= 1 && n <= pages) setPage(n);
                else setPageInput(String(page));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
            />
            <span className="mono muted" style={{ fontSize: '0.8rem' }}>
              / {pages}
            </span>
          </div>
          <button
            className="btn btn--sm"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
        </footer>
      ) : null}
    </div>
  );
}

/**
 * A slide deck or document, as its text and its pictures.
 *
 * Every line is rendered as text, exactly as the file spells it — no markup is
 * interpreted, because a file from outside the app is never trusted to say how
 * it should be drawn. Pictures are handed to the browser as bytes, which is
 * the one safe way to show something a stranger made.
 */
function DocumentReader({ text, scale }: { text: DocumentText; scale: number }) {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const made = new Map<string, string>();
    for (const section of text.sections) {
      for (const image of section.images) {
        if (made.has(image.name)) continue;
        made.set(image.name, URL.createObjectURL(new Blob([new Uint8Array(image.bytes)], { type: image.mime })));
      }
    }
    setUrls(made);
    return () => {
      for (const url of made.values()) URL.revokeObjectURL(url);
    };
  }, [text]);

  const nothing = text.sections.every((s) => s.lines.length === 0 && s.images.length === 0);

  return (
    <div className="reader" style={{ fontSize: `${scale}rem` }}>
      {text.note ? <p className="reader__note">{text.note}</p> : null}

      {nothing ? (
        <Banner kind="warn">
          <strong>Nothing to show in this one</strong>
          <div>
            It has no text and no pictures the app can pull out — everything in it is drawn with
            shapes. Export it to PDF to see it as it looks.
          </div>
        </Banner>
      ) : null}

      {text.sections.map((section, index) => (
        <section key={index} className="reader__section">
          {section.title ? <h3 className="reader__title">{section.title}</h3> : null}
          {section.lines.map((line, i) => (
            <p key={i} className="reader__line">
              {line}
            </p>
          ))}
          {section.images.map((image) => {
            const url = urls.get(image.name);
            return url ? (
              <img
                key={image.name}
                src={url}
                alt={`Picture on ${section.title || 'this page'}`}
                className="reader__image"
                // A chapter deck is fifty pictures; decode them as they are reached.
                loading="lazy"
              />
            ) : null;
          })}
        </section>
      ))}
    </div>
  );
}
